import { InvoiceModel, Model, ModelRecord, VisitModel } from "@common/models";
import { DateTimeUtils } from "@common/utils";
import { StringUtility } from "@common/utils/stringUtility";
import { defaultTo } from "lodash";
import { DateTime } from "luxon";
import { CommunicationType } from "./Enums";
import { IAddClientEmailRequest } from "./requests";
export interface IInvoiceListingModel extends Model {// Response// Request // used to match backend classname.
    dateDueUTC: string
    dateFullyPaidUTC?: string | null
    invoiceId: number
    visitId: number
    petNames: string
    amount: number
    amountDue: number
    hospitalId?: number | null
    hospitalName: string

    clientId: number
    clientFirstName: string
    clientLastName: string

    veterinarianId?: number | null
    vetFirstName: string
    vetLastName: string
    storeTimezoneName: string
    dateCreated: DateTime | string
    userCreated: string | undefined
    dateUpdated: DateTime | string | undefined
    userUpdated: string | undefined
    paymentMethod?: string | undefined
    invoiceDiscrepancyReasonId?: number | null

    invoiceType?: string | undefined;
}

export class InvoiceListingModel extends Model implements ModelRecord<IInvoiceListingModel> {
    // listing means a partial of a bigger model. it is fast.
    // flat version of a complex object ... i.e. not nested.
    dateDueUTC: DateTime | undefined
    dateFullyPaidUTC: DateTime | undefined
    invoiceId: number
    visitId: number
    petNames: string | undefined
    amount: number | undefined
    amountDue: number | undefined
    hospitalId: number | undefined
    hospitalName: string | undefined

    clientId: number | undefined
    clientFirstName: string | undefined
    clientLastName: string | undefined

    veterinarianId: number | undefined
    vetFirstName: string | undefined
    vetLastName: string | undefined

    clientName: string | undefined
    vetName: string | undefined
    status: string | undefined
    storeTimezoneName: string | undefined

    dateDueAtStore: DateTime | undefined;

    dateCreated: DateTime;
    userCreated: string | undefined;
    dateUpdated: DateTime | undefined;
    userUpdated: string | undefined;
    paymentMethod: string | undefined;
    invoiceDiscrepancyReasonId: number | null;

    invoiceType: string | undefined;

    constructor(params: IInvoiceListingModel) {
        super()
        this.storeTimezoneName = params.storeTimezoneName;
        this.dateDueUTC = DateTime.fromISO(params.dateDueUTC, { zone: 'UTC' }) // log here // show fake date // throw runtime // error boundaries
        this.dateDueAtStore = DateTime.fromISO(this.dateDueUTC.toISO(), { zone: this.storeTimezoneName }); //covnert the due date to the time at the store (should be end of day at that store)
        this.dateFullyPaidUTC = params.dateFullyPaidUTC ? DateTime.fromISO(params.dateFullyPaidUTC, { zone: 'UTC' }) : undefined

        this.dateCreated = DateTimeUtils.getAsDateTimeUTC(params.dateCreated)!
        this.userCreated = params.userCreated ?? undefined;
        this.dateUpdated = params.dateUpdated == null ? undefined : DateTimeUtils.getAsDateTimeUTC(params.dateUpdated)!;
        this.userUpdated = params.userUpdated ?? undefined;

        this.invoiceId = params.invoiceId
        this.visitId = params.visitId
        this.petNames = params.petNames
        this.amount = params.amount
        this.amountDue = params.amountDue
        this.hospitalId = defaultTo(params.hospitalId, 0)
        this.hospitalName = params.hospitalName
        this.storeTimezoneName = params.storeTimezoneName;
        this.clientId = params.clientId
        this.clientFirstName = params.clientFirstName
        this.clientLastName = params.clientLastName

        this.veterinarianId = defaultTo(params.veterinarianId, 0)
        this.vetFirstName = params.vetFirstName
        this.vetLastName = params.vetLastName
        this.paymentMethod = params.paymentMethod;
        // new properties
        this.clientName = StringUtility.formattedUserName(params.clientFirstName, params.clientLastName, undefined);
        this.vetName = StringUtility.formattedUserName(params.vetFirstName, params.vetLastName, "Dr.");
        this.invoiceDiscrepancyReasonId = params.invoiceDiscrepancyReasonId ?? null;
        this.invoiceType = defaultTo(params.invoiceType, undefined);

        this.status = this.dateFullyPaidUTC
            ? "Paid"
            : this.dateDueUTC && this.dateDueUTC < DateTime.utc()
                ? "Due"
                : "Open";
    }

    static fromInvoiceModel(invoice: InvoiceModel, hospitalId: number, hospitalName: string, timeZone: string): InvoiceListingModel {
        return new InvoiceListingModel({
            dateDueUTC: invoice.dateDueUTC.toISODate(),
            dateFullyPaidUTC: invoice.dateFullyPaidUTC?.toISODate(),
            invoiceId: invoice.invoiceId,
            visitId: invoice.visitId,
            petNames: invoice.petNames,
            amount: invoice.amount,
            amountDue: invoice.amountDue,
            hospitalId: hospitalId,
            hospitalName: hospitalName,
            clientId: invoice.clientId,
            clientFirstName: invoice.clientFirstName,
            clientLastName: invoice.clientLastName,
            veterinarianId: invoice.veterinarianId,
            vetFirstName: invoice.vetFirstName,
            vetLastName: invoice.vetLastName,
            rowVersion: invoice.rowVersion,
            storeTimezoneName: timeZone,
            dateCreated: invoice.dateCreated,
            dateUpdated: invoice.dateUpdated,
            userUpdated: invoice.userUpdated,
            userCreated: invoice.userCreated
        })
    }

    toClientEmailRequest(visit: VisitModel): IAddClientEmailRequest {
        return {
            visitId: visit.visitId,
            clientId: this.clientId!,
            visitSalesItemId: undefined,
            petId: visit.petId,
            invoiceId: this.invoiceId!,
            emailAddress: visit.client.emailAddress,
            salesDate: visit.visitDate!.toLocaleString(DateTime.DATE_SHORT),
            communicationTypeId: CommunicationType.Invoice,
            communicationTypeName: CommunicationType.displayName(CommunicationType.Invoice),
            storageContentUrl: undefined,
            includeRabiesCertificate: false
        }
    }
}
